import React, { useEffect, useRef, useState } from 'react';
import { Search, X, Loader2, Mic, FileText, Image as ImageIcon } from 'lucide-react';
import { commsApi } from '@/utils/commsApi';
import { formatTime } from '@/utils/commsFormat';
import { SearchFilters, hasFilters, whenToRange } from './SearchFilters';

const RESULT_LIMIT = 100;

export const isSearchActive = (query, filters) => query.trim().length >= 2 || hasFilters(filters);

const Highlight = ({ text, term }) => {
  if (!term) return text;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return text;
  const start = Math.max(0, idx - 40);
  const before = (start > 0 ? '…' : '') + text.slice(start, idx);
  const match = text.slice(idx, idx + term.length);
  const after = text.slice(idx + term.length);
  return <>{before}<mark>{match}</mark>{after}</>;
};

const TypeIcon = ({ result }) => {
  if (result.message_type === 'voice') return <Mic size={12} />;
  if (result.message_type === 'file') return result.is_image ? <ImageIcon size={12} /> : <FileText size={12} />;
  return null;
};

// Search box + Where/From/Type/When chips + results across all conversations.
export const MessageSearch = ({ query, onQueryChange, filters, onFiltersChange, conversations, userId, onOpenResult }) => {
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const active = isSearchActive(query, filters);

  useEffect(() => {
    const term = query.trim();
    abortRef.current?.abort();
    if (!isSearchActive(query, filters)) { setResults([]); setSearching(false); setError(null); return undefined; }
    const controller = new AbortController();
    abortRef.current = controller;
    setSearching(true);
    const params = {
      q: term.length >= 2 ? term : undefined,
      conversation: filters.where || undefined,
      from_user: filters.from || undefined,
      type: filters.type || undefined,
      ...whenToRange(filters.when),
      limit: RESULT_LIMIT,
    };
    const timer = setTimeout(() => {
      commsApi.search(params, controller.signal)
        .then((r) => { setResults(r); setError(null); })
        .catch((err) => { if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') setError('Search failed'); })
        .finally(() => { if (!controller.signal.aborted) setSearching(false); });
    }, 300);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, filters]);

  const term = query.trim().length >= 2 ? query.trim() : '';
  return (
    <>
      <div className="msg-search">
        <Search size={16} />
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search messages, voice transcripts, files"
          aria-label="Search messages"
          data-testid="message-search-input"
        />
        {query && (
          <button type="button" className="msg-search-clear" onClick={() => onQueryChange('')} aria-label="Clear search" data-testid="message-search-clear">
            <X size={14} />
          </button>
        )}
      </div>
      <SearchFilters filters={filters} onChange={onFiltersChange} conversations={conversations} userId={userId} />
      {active && (
        <div className="search-results" data-testid="search-results">
          {searching && <div className="contacts-empty"><Loader2 size={14} className="spin" /> Searching…</div>}
          {!searching && error && <div className="contacts-empty">{error}</div>}
          {!searching && !error && results.length === 0 && (
            <div className="contacts-empty" data-testid="search-empty">
              {term ? <>No messages match “{term}”{hasFilters(filters) ? ' with these filters' : ''}.</> : 'No messages match these filters.'}
            </div>
          )}
          {!searching && results.length >= RESULT_LIMIT && (
            <div className="search-note" data-testid="search-limit-note">Showing the latest {RESULT_LIMIT} matches — narrow with filters to see more.</div>
          )}
          {!searching && results.map((r) => (
            <button type="button" key={r.id} className="search-result" onClick={() => onOpenResult(r)} data-testid={`search-result-${r.id}`}>
              <div className="contact-row">
                <span className="result-conv">{r.conversation_name}</span>
                <span className="contact-time">{formatTime(r.created_at)}</span>
              </div>
              <div className="result-snippet">
                <span className="result-from">{r.from_user_name}:</span> <TypeIcon result={r} /> <Highlight text={r.snippet} term={term} />
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
};
